import { useEffect, useState } from 'react';
import {
  listTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  uploadAdminImage,
} from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';
import { cx } from '../../components/shared/ui/utils';

const emptyForm = {
  id: null,
  name: '',
  role: '',
  rating: '4.8',
  review: '',
  avatarUrl: '',
  isActive: true,
};

const AdminPackageTestimonials = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await listTestimonials();
      setItems(res?.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load testimonials');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectItem = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      role: item.role || '',
      rating: item.rating || '4.8',
      review: item.review || '',
      avatarUrl: item.avatar_url || '',
      isActive: item.is_active !== false,
    });
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = {
        name: form.name,
        role: form.role,
        rating: form.rating,
        review: form.review,
        avatar_url: form.avatarUrl,
        is_active: form.isActive,
      };
      if (form.id) await updateTestimonial(form.id, payload);
      else await createTestimonial(payload);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await deleteTestimonial(form.id);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, avatarUrl: res.path }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Testimonials</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setForm(emptyForm)}>
            New testimonial
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectItem(item)}
              className={cx(
                'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm',
                form.id === item.id ? 'bg-slate-100' : 'hover:bg-slate-50'
              )}
            >
              <span className="font-semibold text-slate-900">{item.name}</span>
              <span className="text-xs text-slate-500">{item.role}</span>
            </button>
          ))}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Role"
            value={form.role}
            onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Rating"
            value={form.rating}
            onChange={(e) => setForm((prev) => ({ ...prev, rating: e.target.value }))}
          />
          <textarea
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            rows={3}
            placeholder="Review"
            value={form.review}
            onChange={(e) => setForm((prev) => ({ ...prev, review: e.target.value }))}
          />
          <div className="flex items-center gap-3 text-sm">
            <input type="file" onChange={handleUpload} />
            {form.avatarUrl && <span className="text-xs text-slate-500">Avatar selected</span>}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            {form.id && (
              <Button variant="outline" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={handleSave}>Save testimonial</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackageTestimonials;
